import { SimNaoEnum, simNaoEnumToBoolean } from 'components/form'
import {
  IvcfInput,
  IvcfPerguntaEnum,
  MarcadorConsumoAlimentar,
  MedicoesInput,
  ObjetivoInput,
  ResultadoExameInput,
  TipoAtendimentoProfissional,
} from 'graphql/types.generated'
import { partition } from 'util/array'
import { isUndefinedOrNull } from 'util/checks'
import { formatNumber, parseNumber } from 'util/number'
import { v4 as uuidv4 } from 'uuid'
import { AntropometriaAccordionModel } from 'view/atendimentos/components/MedicoesPanel/AntropometriaAccordion'
import { SinaisVitaisAccordionModel } from 'view/atendimentos/components/MedicoesPanel/SinaisVitaisAccordion'

import { MedicoesRetificacaoQueryModel } from '../../components/modals/medicoes/model-medicoes'
import { MEDICOES_EXAME_FISICO } from '../../historico/model/model-historico'
import {
  convertAnexoArquivoQueryRetificacaoToForm,
  convertAnexoArquivosToInput,
} from './anexo-arquivos/converter-anexoArquivos'
import { ArquivoAnexadoQueryModel } from './anexo-arquivos/model-anexoArquivos'
import { convertIvcfQueryRetificacaoToForm } from './ivcf/converter-ivcf'
import { IvcfFieldModel, IvcfRetificacaoQueryModel } from './ivcf/model-ivcf'
import {
  convertMCAQueryRetificacaoToForm,
  convertMCAQueryToForm,
  convertMCAToInput,
} from './marcadores-consumo-alimentar/converter-mca'
import { EvolucaoObjetivoQueryModel, FormAtivoObjetivoEnum, SwitchButtonObjetivoFormModel } from './model'
import { ObjetivoFormModel } from './ObjetivoForm'
import { convertPuericulturaToInput } from './puericultura/converter-objetivo'
import {
  ResultadoExameComSolicitacaoModel,
  ResultadoExameEspecificoModel,
  ResultadoExameQueryModel,
  ResultadoExameSemSolicitacaoModel,
  ResultadosExamesModel,
  ResultadosExamesNaoSolicitadosModel,
  ResultadosExamesSolicitadosModel,
} from './resultados-exames/model-resultadosExames'
import {
  addPrefix,
  getResultadoExameEspecificoConverterToInput,
  getResultadoExameEspecificoConverterToModel,
  isExameEspecifico,
  removePrefix,
} from './resultados-exames/util-resultadosExames'

export const convertObjetivoFormToInput = (
  form: ObjetivoFormModel,
  isObservacaoAndHasPuericultura: boolean,
  dataNascimentoCidadao: number,
  dataReferencia: number
): ObjetivoInput =>
  form && {
    texto: form.texto,
    medicoes: convertMedicoesToInput(form, isObservacaoAndHasPuericultura),
    marcadoresConsumoAlimentar: convertMCAToInput(form, dataNascimentoCidadao, dataReferencia),
    necessidadesEspeciais: form.necessidadesEspeciais,
    puericultura:
      !isObservacaoAndHasPuericultura && form.atendimentosEspecificos?.formAtivo === FormAtivoObjetivoEnum.PUERICULTURA
        ? convertPuericulturaToInput(form.atendimentosEspecificos.puericultura)
        : null,
    resultadosExame: form?.resultadosExames && convertResultadosExamesToInput(form.resultadosExames),
    ivcf: form?.atendimentosEspecificos?.ivcf && convertIvcfToInput(form?.atendimentosEspecificos?.ivcf),
    anexoArquivos: convertAnexoArquivosToInput(form.anexoArquivos),
  }

const convertIvcfToInput = (ivcf: IvcfFieldModel): IvcfInput => {
  return {
    aplicacao: Object.entries(ivcf.aplicacao)
      .filter(([, resposta]: [IvcfPerguntaEnum, SimNaoEnum]) => !isUndefinedOrNull(resposta))
      .map(([pergunta, resposta]: [IvcfPerguntaEnum, SimNaoEnum]) => ({
        pergunta,
        resposta: simNaoEnumToBoolean[resposta],
      })),
  }
}

export const convertMedicoesToInput = (
  form: ObjetivoFormModel,
  isObservacaoAndHasPuericultura: boolean = false
): MedicoesInput => {
  return {
    dum: isObservacaoAndHasPuericultura ? null : form.dum,
    vacinacaoEmDia: simNaoEnumToBoolean[form.vacinacaoEmDia],
    ...convertMedicoesPanelToInput(form.sinaisVitais, form.antropometria),
  }
}

export const convertMedicoesPanelToInput = (
  sinaisVitais: SinaisVitaisAccordionModel,
  antropometria: AntropometriaAccordionModel
): MedicoesInput => ({
  altura: parseNumber(antropometria?.altura),
  circunferenciaAbdominal: parseNumber(antropometria?.circunferenciaAbdominal),
  frequenciaCardiaca: parseNumber(sinaisVitais?.frequenciaCardiaca),
  frequenciaRespiratoria: parseNumber(sinaisVitais?.frequenciaRespiratoria),
  perimetroCefalico: parseNumber(antropometria?.perimetroCefalico),
  perimetroPanturrilha: parseNumber(antropometria?.perimetroPanturrilha),
  peso: parseNumber(antropometria?.peso),
  pressaoArterialDiastolica: parseNumber(sinaisVitais?.pressaoArterialDiastolica),
  pressaoArterialSistolica: parseNumber(sinaisVitais?.pressaoArterialSistolica),
  saturacaoO2: parseNumber(sinaisVitais?.saturacaoO2),
  glicemia: parseNumber(sinaisVitais?.glicemia),
  temperatura: parseNumber(sinaisVitais?.temperatura),
  tipoGlicemia: sinaisVitais?.tipoGlicemia,
  valorForcaMuscMaoDireita:
    antropometria?.valorForcaMuscMaoDireita && parseNumber(antropometria?.valorForcaMuscMaoDireita.toString()),
  valorForcaMuscMaoEsquerda:
    antropometria?.valorForcaMuscMaoEsquerda && parseNumber(antropometria?.valorForcaMuscMaoEsquerda.toString()),
  unidadeForcaMuscMaoDireita: antropometria?.unidadeForcaMuscMaoDireita?.enum,
  unidadeForcaMuscMaoEsquerda: antropometria?.unidadeForcaMuscMaoEsquerda?.enum,
  exameFisico: convertExameFisicoToInput(antropometria),
})

const convertExameFisicoToInput = (antropometria: AntropometriaAccordionModel) => {
  const hasAnyExameFisico = MEDICOES_EXAME_FISICO.some((campo) => !isUndefinedOrNull(antropometria[campo]))

  if (!hasAnyExameFisico) return null

  return {
    olhoDireito: antropometria?.olhoDireito,
    olhoEsquerdo: antropometria?.olhoEsquerdo,
    ouvidoDireito: antropometria?.ouvidoDireito,
    ouvidoEsquerdo: antropometria?.ouvidoEsquerdo,
    membroInferiorDireito: antropometria?.membroInferiorDireito,
    membroInferiorEsquerdo: antropometria?.membroInferiorEsquerdo,
    pele: antropometria?.pele,
  }
}

export type ConverterResultadoExameType = ResultadoExameComSolicitacaoModel &
  ResultadoExameSemSolicitacaoModel &
  ResultadoExameEspecificoModel

export const convertResultadosExamesToInput = (values: ResultadosExamesModel): ResultadoExameInput[] => {
  const { resultadosComSolicitacao, resultadosSemSolicitacao } = values

  const resultadosComSolicitacaoInput = resultadosComSolicitacao
    ? Object.entries(resultadosComSolicitacao).map(([id, value]) =>
        convertResultadoExameToInput(value as ConverterResultadoExameType, id)
      )
    : []

  const resultadosSemSolicitacaoInput =
    Object.values(resultadosSemSolicitacao ?? {}).map((value) => convertResultadoExameToInput(value)) ?? []

  return [...resultadosComSolicitacaoInput, ...resultadosSemSolicitacaoInput]
}

export const convertResultadoExameToInput = (value: ConverterResultadoExameType, id?: ID): ResultadoExameInput => {
  const { dataRealizado, dataResultado, dataSolicitado, descricao, exame = undefined, resultado, editingId } = value

  const isSemSolicitacao = !id
  const isEspecifico = isExameEspecifico(exame)

  const converter = getResultadoExameEspecificoConverterToInput(exame)

  return {
    id: !isUndefinedOrNull(editingId) ? editingId : !isSemSolicitacao ? removePrefix(id) : null,
    exameId: isSemSolicitacao && isUndefinedOrNull(editingId) ? exame.id : null,
    dataResultado,
    dataRealizacao: dataRealizado,
    dataSolicitacao: dataSolicitado,
    resultado: isEspecifico ? descricao : resultado,
    especifico: isEspecifico ? converter(value) : undefined,
  }
}

export const convertResultadosExamesQueryToForm = (
  inputs: ReadonlyArray<ResultadoExameQueryModel>
): ResultadosExamesModel => {
  const [comSolicitacao, semSolicitacao] = partition(inputs, (input) => !!input.solicitacao)

  return {
    resultadosSemSolicitacao: convertResultadosExamesSemSolicitacaoQueryToModel(semSolicitacao),
    resultadosComSolicitacao: convertResultadosExamesComSolicitacaoQueryToModel(comSolicitacao),
  }
}

const convertResultadosExamesComSolicitacaoQueryToModel = (
  inputs: ResultadoExameQueryModel[]
): ResultadosExamesSolicitadosModel => {
  return inputs
    ? Object.fromEntries(
        inputs.map((input) => {
          const { id } = input

          return [
            addPrefix(id),
            // @diegoasmello fix cast https://github.com/laboratoriobridge/pec/issues/11424
            convertResultadoExameComSolicitacaoQueryToModel(input) as ResultadoExameComSolicitacaoModel &
              ResultadoExameEspecificoModel,
          ]
        })
      )
    : {}
}

const convertResultadoExameComSolicitacaoQueryToModel = (
  input: ResultadoExameQueryModel
): ResultadoExameComSolicitacaoModel | ResultadoExameEspecificoModel => {
  const { id, exame, resultado, dataRealizacao, dataResultado } = input

  const isEspecifico = isExameEspecifico(exame)

  if (isEspecifico) {
    return convertResultadoExameEspecificoQueryToModel(input, true)
  }

  return {
    editingId: id,
    exame,
    resultado,
    dataRealizado: dataRealizacao,
    dataResultado,
  } as ResultadoExameComSolicitacaoModel
}

const convertResultadosExamesSemSolicitacaoQueryToModel = (
  inputs: ResultadoExameQueryModel[]
): ResultadosExamesNaoSolicitadosModel => {
  return inputs
    ? Object.fromEntries(
        inputs.map((input) => {
          return [
            uuidv4(),
            convertResultadoExameSemSolicitacaoQueryToModel(input) as ResultadoExameSemSolicitacaoModel &
              ResultadoExameEspecificoModel,
          ]
        })
      )
    : {}
}

const convertResultadoExameSemSolicitacaoQueryToModel = (
  input: ResultadoExameQueryModel
): ResultadoExameSemSolicitacaoModel | ResultadoExameEspecificoModel => {
  const { id, exame, dataRealizacao, dataResultado, dataSolicitacao, resultado } = input

  const isEspecifico = isExameEspecifico(exame)

  if (isEspecifico) {
    return convertResultadoExameEspecificoQueryToModel(input, false)
  }

  return {
    editingId: id,
    exame,
    resultado,
    dataResultado,
    dataRealizado: dataRealizacao,
    dataSolicitado: dataSolicitacao,
  } as ResultadoExameSemSolicitacaoModel
}

const convertResultadoExameEspecificoQueryToModel = (
  input: ResultadoExameQueryModel,
  hasSolicitacao: boolean
): ResultadoExameEspecificoModel => {
  const { id, exame, dataRealizacao, dataResultado, dataSolicitacao, resultado, especifico } = input

  const converterToModel = getResultadoExameEspecificoConverterToModel(exame)
  const resultadoValue = converterToModel(especifico, exame)

  return {
    editingId: id,
    exame,
    resultado: resultadoValue,
    descricao: resultado,
    dataResultado,
    dataRealizado: dataRealizacao,
    dataSolicitado: hasSolicitacao ? null : dataSolicitacao,
  }
}

const convertTipoAtendimentoToAtendimentosEspecificos = (
  prevValue: SwitchButtonObjetivoFormModel,
  tipoAtendimento: TipoAtendimentoProfissional,
  hasIvcfPreenchidoEmAtendObservacaoAnterior: boolean
): SwitchButtonObjetivoFormModel => {
  const switchFormAtivo = (tipoAtendimentoProfissional: TipoAtendimentoProfissional) => {
    switch (tipoAtendimentoProfissional) {
      case TipoAtendimentoProfissional.PRE_NATAL:
        return FormAtivoObjetivoEnum.PRE_NATAL
      case TipoAtendimentoProfissional.PUERICULTURA:
        return FormAtivoObjetivoEnum.PUERICULTURA
      default:
        return hasIvcfPreenchidoEmAtendObservacaoAnterior ? FormAtivoObjetivoEnum.IVCF : null
    }
  }
  return {
    ...prevValue,
    formAtivo: switchFormAtivo(tipoAtendimento),
  }
}

export const convertObjetivoQueryToForm = (
  prevValue: ObjetivoFormModel,
  tipoAtendimento: TipoAtendimentoProfissional,
  hasIvcfPreenchidoEmAtendObservacaoAnterior: boolean,
  {
    evolucaoObjetivo,
    resultadosExames,
    marcadorConsumoAlimentar,
  }: {
    evolucaoObjetivo: EvolucaoObjetivoQueryModel
    resultadosExames: ReadonlyArray<ResultadoExameQueryModel>
    marcadorConsumoAlimentar: MarcadorConsumoAlimentar
  }
): ObjetivoFormModel => {
  return {
    ...prevValue,
    texto: evolucaoObjetivo?.descricao,
    resultadosExames: convertResultadosExamesQueryToForm(resultadosExames),
    marcadoresConsumoAlimentar: convertMCAQueryToForm(marcadorConsumoAlimentar),
    atendimentosEspecificos: convertTipoAtendimentoToAtendimentosEspecificos(
      prevValue.atendimentosEspecificos,
      tipoAtendimento,
      hasIvcfPreenchidoEmAtendObservacaoAnterior
    ),
  }
}

export const convertSinaisVitaisQueryRetificacaoToForm = (
  medicoes: MedicoesRetificacaoQueryModel
): SinaisVitaisAccordionModel => {
  const [pressaoArterialSistolica, pressaoArterialDiastolica] = medicoes?.valorPressaoArterial?.split('/') || []
  return {
    frequenciaCardiaca: medicoes?.valorFrequenciaCardiaca?.toString(),
    frequenciaRespiratoria: medicoes?.valorFrequenciaRespiratoria?.toString(),
    pressaoArterialSistolica: pressaoArterialSistolica,
    pressaoArterialDiastolica: pressaoArterialDiastolica,
    saturacaoO2: medicoes?.valorSaturacaoO2?.toString(),
    glicemia: medicoes?.valorGlicemia?.toString(),
    temperatura: medicoes?.valorTemperatura ? formatNumber(medicoes.valorTemperatura, 1, false) : undefined,
    tipoGlicemia: medicoes?.tipoGlicemia,
  }
}

export const convertAntropometriaQueryRetificacaoToForm = (
  medicoes: MedicoesRetificacaoQueryModel
): AntropometriaAccordionModel => {
  return {
    altura: medicoes?.valorAltura ? formatNumber(medicoes.valorAltura, 1, false) : undefined,
    circunferenciaAbdominal: medicoes?.valorCircunferenciaAbdominal
      ? formatNumber(medicoes.valorCircunferenciaAbdominal, 1, false)
      : undefined,
    perimetroCefalico: medicoes?.valorPerimetroCefalico
      ? formatNumber(medicoes.valorPerimetroCefalico, 1, false)
      : undefined,
    perimetroPanturrilha: medicoes?.perimetroPanturrilha
      ? formatNumber(medicoes.perimetroPanturrilha, 1, false)
      : undefined,
    peso: medicoes?.valorPeso ? formatNumber(medicoes.valorPeso, 3, false) : undefined,
    unidadeForcaMuscMaoDireita: undefined,
    valorForcaMuscMaoDireita: undefined,
    unidadeForcaMuscMaoEsquerda: undefined,
    valorForcaMuscMaoEsquerda: undefined,
    pele: undefined,
    olhoDireito: undefined,
    olhoEsquerdo: undefined,
    ouvidoDireito: undefined,
    ouvidoEsquerdo: undefined,
    membroInferiorDireito: undefined,
    membroInferiorEsquerdo: undefined,
  }
}

export const convertObjetivoQueryRetificacaoToForm = (
  evolucaoObjetivo: EvolucaoObjetivoQueryModel,
  marcadorConsumoAlimentar: MarcadorConsumoAlimentar,
  medicaoAtual: MedicoesRetificacaoQueryModel,
  ivcf: IvcfRetificacaoQueryModel,
  arquivosAnexados: ReadonlyArray<ArquivoAnexadoQueryModel>
): ObjetivoFormModel => {
  const valorVacinacaoEmDia = medicaoAtual?.valorVacinacaoEmDia
  return {
    texto: evolucaoObjetivo?.descricao,
    marcadoresConsumoAlimentar: { ...convertMCAQueryRetificacaoToForm(marcadorConsumoAlimentar), openedAccordions: [] },
    sinaisVitais: convertSinaisVitaisQueryRetificacaoToForm(medicaoAtual),
    antropometria: convertAntropometriaQueryRetificacaoToForm(medicaoAtual),
    ...(!isUndefinedOrNull(valorVacinacaoEmDia) && {
      vacinacaoEmDia: valorVacinacaoEmDia ? SimNaoEnum.SIM : SimNaoEnum.NAO,
    }),
    atendimentosEspecificos: ivcf
      ? {
          ivcf: convertIvcfQueryRetificacaoToForm(ivcf),
          formAtivo: FormAtivoObjetivoEnum.IVCF,
          puericultura: null,
        }
      : {
          ivcf: null,
          formAtivo: null,
          puericultura: null,
        },
    anexoArquivos: arquivosAnexados.map(convertAnexoArquivoQueryRetificacaoToForm),
  }
}
